import { Suspense, lazy, useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { motion, useReducedMotion, useScroll } from "framer-motion";
import Lenis from "lenis";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ChatWidget from "@/components/ChatWidget";
import Home from "@/pages/Home";

const About = lazy(() => import("@/pages/About"));
const WaitingList = lazy(() => import("@/pages/WaitingList"));
const Careers = lazy(() => import("@/pages/Careers"));
const ResidentInquiry = lazy(() => import("@/pages/ResidentInquiry"));
const Services = lazy(() => import("@/pages/Services"));
const SeniorLiving = lazy(() => import("@/pages/SeniorLiving"));
const Veterans = lazy(() => import("@/pages/Veterans"));
const RoomsPricing = lazy(() => import("@/pages/RoomsPricing"));
const Life = lazy(() => import("@/pages/Life"));
const FAQ = lazy(() => import("@/pages/FAQ"));
const ReferralPartners = lazy(() => import("@/pages/ReferralPartners"));
const Contact = lazy(() => import("@/pages/Contact"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const Accessibility = lazy(() => import("@/pages/Accessibility"));
const NotFound = lazy(() => import("@/pages/NotFound"));

const ScrollProgress = () => {
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll();
  if (reduce) return null;
  return (
    <motion.div
      aria-hidden="true"
      className="fixed top-0 left-0 right-0 h-1 z-[80] origin-left bg-gradient-to-r from-laurel via-gold to-laurel"
      style={{ scaleX: scrollYProgress }}
    />
  );
};

const ScrollManager = () => {
  const { pathname } = useLocation();
  const lenisRef = useRef(null);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const lenis = new Lenis({ duration: 1.05, smoothWheel: true });
    lenisRef.current = lenis;
    let id;
    const raf = (t) => {
      lenis.raf(t);
      id = requestAnimationFrame(raf);
    };
    id = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(id);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (lenisRef.current) {
      lenisRef.current.scrollTo(0, { immediate: true });
    }
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

const AnimatedRoutes = () => {
  const location = useLocation();
  const reduce = useReducedMotion();
  return (
    <motion.main
      id="main-content"
      key={location.pathname}
      initial={reduce ? false : { opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
    >
      <Suspense fallback={<div className="min-h-[60vh]" aria-hidden="true" />}>
        <Routes location={location}>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/waiting-list" element={<WaitingList />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/resident-inquiry" element={<ResidentInquiry />} />
          <Route path="/services" element={<Services />} />
          <Route path="/senior-living" element={<SeniorLiving />} />
          <Route path="/veteran-housing" element={<Veterans />} />
          <Route path="/rooms-pricing" element={<RoomsPricing />} />
          <Route path="/life-at-savannahs-house" element={<Life />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/referral-partners" element={<ReferralPartners />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/accessibility" element={<Accessibility />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </motion.main>
  );
};

function App() {
  return (
    <BrowserRouter>
      <a href="#main-content" className="skip-link" data-testid="skip-to-content">
        Skip to main content
      </a>
      <ScrollManager />
      <ScrollProgress />
      <Header />
      <AnimatedRoutes />
      <Footer />
      <ChatWidget />
    </BrowserRouter>
  );
}

export default App;
